import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { UserType } from '../../types';
import { DataChannelMessageType, DataChannelSessionIntentionPromptMessage, useSendMessage } from '../../video-sdk';
import { participantUserIdsAtom } from '../../video-sdk/core/room/participant/atoms';
import { inSessionPatientInformationAtom, providerShortIdAtom } from '../state';

export function useSendSessionIntentionPromptCallback() {
  const getProviderShortId = useAtomCallback(useCallback((get) => get(providerShortIdAtom), []));
  const getParticipantUserIds = useAtomCallback(useCallback((get) => get(participantUserIdsAtom), []));
  const getInSessionPatientInfo = useAtomCallback(useCallback((get) => get(inSessionPatientInformationAtom), []));
  const sendMessage = useSendMessage();
  const setInSessionPatientInformation = useSetAtom(inSessionPatientInformationAtom);

  return useCallback(
    async (version: number) => {
      const issuedAt = new Date().toISOString();
      const message: DataChannelSessionIntentionPromptMessage = {
        type: DataChannelMessageType.SESSION_INTENTION_PROMPT,
        sender: {
          entityId: getProviderShortId() ?? '',
          entityType: UserType.PROVIDER,
        },
        prompt: {
          version,
          issuedAt,
        },
      };

      const didSendPromptMessage = await sendMessage(message);

      if (didSendPromptMessage) {
        const participantUserIds = getParticipantUserIds();
        const inSessionPatient = getInSessionPatientInfo();
        if (inSessionPatient && participantUserIds.includes(inSessionPatient.visitorUuid || '')) {
          setInSessionPatientInformation({
            ...inSessionPatient,
            intentionPromptSentAt: issuedAt,
          });
        }
      }

      return didSendPromptMessage;
    },
    [sendMessage, getProviderShortId, getParticipantUserIds, getInSessionPatientInfo, setInSessionPatientInformation],
  );
}
